import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EventsRule, RuleItemRef } from '../../../../models/scan-testing.model';
import { RuleEvent } from './set-rules.component';

// Selects the events and plans permitted to scan at a gate. Selecting an event
// checkbox toggles the whole event; plans can also be picked individually.
@Component({
  selector: 'app-sr-events-modal',
  templateUrl: './sr-events-modal.component.html',
  standalone: false,
  styleUrls: ['./sr-modal-shared.scss']
})
export class SrEventsModalComponent implements OnInit {
  @Input() initial?: EventsRule;
  @Input() events: RuleEvent[] = [];
  @Output() confirmed = new EventEmitter<EventsRule>();
  @Output() cancelled = new EventEmitter<void>();

  searchText = '';
  selectedKeys = new Set<string>();
  expanded = new Set<string>();

  ngOnInit(): void {
    // Restore the saved selection as-is. A whole-event ref ticks only the event
    // checkbox (not its plans) — the event and its plans are independent choices.
    for (const ref of this.initial?.items ?? []) {
      this.selectedKeys.add(this.refKey(ref));
      this.expanded.add(ref.eventName);
    }
  }

  private refKey(ref: RuleItemRef): string {
    return ref.kind === 'plan'
      ? `plan:${ref.eventName}:${ref.plan}`
      : `event:${ref.eventName}`;
  }

  eventKey(ev: RuleEvent): string { return `event:${ev.eventName}`; }
  planKey(ev: RuleEvent, plan: string): string { return `plan:${ev.eventName}:${plan}`; }

  filteredEvents(): RuleEvent[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) { return this.events; }
    return this.events.filter((ev) =>
      ev.eventName.toLowerCase().includes(q)
      || (ev.description || '').toLowerCase().includes(q)
      || ev.plans.some((p) => p.toLowerCase().includes(q)));
  }

  isEventSelected(ev: RuleEvent): boolean { return this.selectedKeys.has(this.eventKey(ev)); }
  isPlanSelected(ev: RuleEvent, plan: string): boolean { return this.selectedKeys.has(this.planKey(ev, plan)); }
  isExpanded(ev: RuleEvent): boolean { return this.expanded.has(ev.eventName); }

  toggleExpand(ev: RuleEvent): void {
    if (this.expanded.has(ev.eventName)) { this.expanded.delete(ev.eventName); }
    else { this.expanded.add(ev.eventName); }
  }

  // Whether every plan under an event is individually selected.
  allPlansSelected(ev: RuleEvent): boolean {
    return ev.plans.length > 0 && ev.plans.every((p) => this.selectedKeys.has(this.planKey(ev, p)));
  }

  // How many of an event's plans are individually picked (for the row's badge).
  selectedPlanCount(ev: RuleEvent): number {
    return ev.plans.reduce((n, p) => n + (this.selectedKeys.has(this.planKey(ev, p)) ? 1 : 0), 0);
  }

  // A footer summary of what will be saved, e.g. "2 events, 3 plans selected".
  selectionSummary(): string {
    let events = 0;
    let plans = 0;
    for (const ev of this.events) {
      if (this.selectedKeys.has(this.eventKey(ev))) { events += 1; continue; }
      plans += this.selectedPlanCount(ev);
    }
    if (!events && !plans) { return 'Nothing selected'; }
    const parts: string[] = [];
    if (events) { parts.push(`${events} event${events === 1 ? '' : 's'}`); }
    if (plans) { parts.push(`${plans} plan${plans === 1 ? '' : 's'}`); }
    return `${parts.join(', ')} selected`;
  }

  // Whole event and individual plans are mutually exclusive per event: an event
  // is allowed either as a whole (all plans, incl. any added later) or as a
  // chosen subset of plans — never both.

  // Toggles the whole event. Selecting it clears any individual plan picks.
  toggleEvent(ev: RuleEvent): void {
    const key = this.eventKey(ev);
    if (this.selectedKeys.has(key)) {
      this.selectedKeys.delete(key);
    } else {
      this.selectedKeys.add(key);
      for (const plan of ev.plans) { this.selectedKeys.delete(this.planKey(ev, plan)); }
    }
  }

  // Toggles a single plan. Picking any plan clears the whole-event selection.
  togglePlan(ev: RuleEvent, plan: string): void {
    const key = this.planKey(ev, plan);
    if (this.selectedKeys.has(key)) {
      this.selectedKeys.delete(key);
    } else {
      this.selectedKeys.delete(this.eventKey(ev));
      this.selectedKeys.add(key);
    }
  }

  // Selects every plan under one event individually (plan-subset mode), or
  // clears them. Also clears the whole-event selection, keeping the two modes
  // mutually exclusive.
  toggleAllPlans(ev: RuleEvent): void {
    if (this.allPlansSelected(ev)) {
      for (const plan of ev.plans) { this.selectedKeys.delete(this.planKey(ev, plan)); }
    } else {
      this.expanded.add(ev.eventName);
      this.selectedKeys.delete(this.eventKey(ev));
      for (const plan of ev.plans) { this.selectedKeys.add(this.planKey(ev, plan)); }
    }
  }

  selectAll(): void {
    for (const ev of this.events) { this.selectedKeys.add(this.eventKey(ev)); }
  }

  clearAll(): void {
    this.selectedKeys.clear();
  }

  expandAll(): void {
    for (const ev of this.events) { this.expanded.add(ev.eventName); }
  }

  onConfirm(): void {
    const items: RuleItemRef[] = [];
    for (const ev of this.events) {
      // A whole-event selection emits a single event ref; otherwise emit each
      // individually-picked plan. Avoids redundant event+plan pairs.
      if (this.selectedKeys.has(this.eventKey(ev))) {
        items.push({ kind: 'event', eventName: ev.eventName });
        continue;
      }
      for (const plan of ev.plans) {
        if (this.selectedKeys.has(this.planKey(ev, plan))) {
          items.push({ kind: 'plan', eventName: ev.eventName, plan });
        }
      }
    }
    this.confirmed.emit({ type: 'events', items });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  trackEvent(_idx: number, ev: RuleEvent): string { return ev.eventName; }
  trackPlan(_idx: number, plan: string): string { return plan; }
}
